import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class UrlValidatorService {
  // Whitelist routes yang diizinkan
  private allowedRoutes = [
    '/dashboard',
    '/userprofile',
    '/hotel',
    '/hotel/hotel-detail',
    '/office-lease',
    '/office-lease/detail-office-lease',
    '/office-lease/tenant-list',
    '/office-lease/tenant-list/business-unit-detail',
    '/golf',
    '/residences',
    '/residences/detail-residences',
    '/sales-activities',
    '/sales-activities/sales-list',
    '/sales-activities/sales-list/detail',
    '/sales-activities/sales-activities-detail',
    '/404',
    '/500'
  ];

  constructor(private router: Router) {}

  /**
   * Validasi apakah URL aman untuk redirect
   * @param url URL yang akan divalidasi
   * @returns URL yang aman atau default URL
   */
  validateReturnUrl(url: string | null | undefined): string {
    const defaultUrl = '/dashboard';
    
    // Jika URL tidak ada, return default
    if (!url) {
      return defaultUrl;
    }

    // Decode URL untuk handle encoded characters
    try {
      url = decodeURIComponent(url);
    } catch (e) {
      console.warn('Failed to decode URL:', url);
      return defaultUrl;
    }

    // Cek apakah URL adalah absolute URL (dimulai dengan http://, https://, //, etc)
    if (this.isAbsoluteUrl(url)) {
      console.warn('Blocked absolute URL redirect attempt:', url);
      return defaultUrl;
    }

    // Cek apakah URL mengandung karakter berbahaya
    if (this.containsDangerousCharacters(url)) {
      console.warn('Blocked URL with dangerous characters:', url);
      return defaultUrl;
    }

    // Normalisasi URL (remove query params dan fragments)
    const normalizedUrl = this.normalizeUrl(url);

    // Cek apakah URL ada di whitelist
    if (this.isUrlInWhitelist(normalizedUrl)) {
      return url; // Return original URL dengan query params jika ada
    }

    // Cek apakah URL adalah sub-route dari whitelist
    if (this.isSubRouteAllowed(normalizedUrl)) {
      return url;
    }

    console.warn('URL not in whitelist:', url);
    return defaultUrl;
  }

  /**
   * Cek apakah URL adalah absolute URL
   */
  private isAbsoluteUrl(url: string): boolean {
    // Pattern untuk mendeteksi absolute URLs
    const absoluteUrlPattern = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
    
    // Cek juga untuk javascript: dan data: protocols
    const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
    
    return absoluteUrlPattern.test(url) || dangerousProtocols.test(url);
  }

  /**
   * Cek apakah URL mengandung karakter berbahaya
   */
  private containsDangerousCharacters(url: string): boolean {
    // Pattern untuk karakter yang bisa digunakan untuk bypass
    const dangerousPattern = /[<>'"\\]/;
    
    // Cek untuk double slashes yang bukan di awal
    const doubleSlashPattern = /\/\/.+/;
    
    // Cek untuk backslashes yang bisa digunakan untuk redirect
    const backslashPattern = /\\/;
    
    return dangerousPattern.test(url) || 
           (doubleSlashPattern.test(url) && !url.startsWith('//')) ||
           backslashPattern.test(url);
  }

  /**
   * Normalisasi URL dengan menghilangkan query params dan fragments
   */
  private normalizeUrl(url: string): string {
    // Remove query params dan fragments
    let normalizedUrl = url.split('?')[0].split('#')[0];
    
    // Remove trailing slash
    if (normalizedUrl.endsWith('/') && normalizedUrl !== '/') {
      normalizedUrl = normalizedUrl.slice(0, -1);
    }
    
    return normalizedUrl;
  }

  /**
   * Cek apakah URL ada di whitelist
   */
  private isUrlInWhitelist(url: string): boolean {
    return this.allowedRoutes.includes(url);
  }

  /**
   * Cek apakah URL adalah sub-route yang diizinkan
   */
  private isSubRouteAllowed(url: string): boolean {
    // Cek apakah URL dimulai dengan salah satu route yang diizinkan
    return this.allowedRoutes.some(allowedRoute => {
      // Exact match atau sub-route
      return url === allowedRoute || url.startsWith(allowedRoute + '/');
    });
  }

  /**
   * Parse dan validasi URL dari Router
   */
  parseAndValidateUrl(url: string): any {
    try {
      const urlTree = this.router.parseUrl(url);
      // Validasi bahwa URL bisa di-parse oleh Angular Router
      if (urlTree) {
        return urlTree;
      }
    } catch (e) {
      console.warn('Failed to parse URL:', url);
    }
    
    // Return default jika parsing gagal
    return this.router.parseUrl('/dashboard');
  }
}